// Sentry init must run before anything else
import './sentry';
// Before any module that builds a zod schema — the generated client included.
import './zod-config';
import './index.css';
import './i18n';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import React from 'react';
import ReactDOM from 'react-dom/client';

import { installApiErrorAdapter } from './api/errors';
import { client as heyApiClient } from './api/generated/client.gen';
import { authorizedClientFetch } from './api/transport';
import { withoutDenial } from './auth/denial';
import { msalInstance } from './auth/msalConfig';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { NotFoundPage } from './components/common/NotFoundPage';
import { RouteErrorPage } from './components/common/RouteErrorPage';
import { API_BASE } from './config';
import { routeTree } from './routeTree.gen';

heyApiClient.setConfig({
  baseUrl: API_BASE,
  fetch: authorizedClientFetch,
});
installApiErrorAdapter(heyApiClient);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
});

const router = createRouter({
  routeTree,
  context: { msalInstance, queryClient },
  // Router-wide, not just on `_auth`: TanStack builds a boundary per match, so
  // this catches a child route's failure inside the layout (nav intact) and the
  // `_auth` loader's own failure full-screen. The `ErrorBoundary` below wraps
  // the RouterProvider from outside and never sees either.
  defaultErrorComponent: RouteErrorPage,
  // An unmatched URL resolves against the root route, so this is what answers
  // it — TanStack's own fallback is an unstyled "Not Found" with no way out.
  defaultNotFoundComponent: NotFoundPage,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

void msalInstance.initialize().then(async () => {
  const redirectResult = await msalInstance
    .handleRedirectPromise()
    .catch(() => null);
  // Only a sign-in that just happened designates the active account.
  //
  // This used to fall back to `getAllAccounts()[0]`. The reason first given was
  // a shared machine — stop the app adopting whoever used the browser last and
  // writing audit rows under their name — and with `cacheLocation` now
  // `localStorage` (msalConfig.ts) that reason holds again: the cache outlives
  // the tab that made it, so a colleague's account really can still be sitting
  // in it. Nothing is lost by refusing the fallback, because MSAL keeps the
  // *active*-account filter in that same store: a session designated here is
  // already active in a new tab, without this bootstrap re-electing anything.
  //
  // What it also fixes: `_auth` clears the active account on a raw 401/403 but
  // cannot clear the cache. Re-electing that account on the next load meant
  // being refused again, sent back here, and re-elected — a loop of the app's
  // own making. With no active account, `_auth` routes to `/login`, and what
  // happens there is Entra's business, not ours: its sign-in page when it has no
  // session of its own, a silent reuse when it has one — `loginRequestFor` omits
  // `prompt` for an ordinary login deliberately, so an operator who is already
  // signed in to Entra is not made to prove it twice. A picker is only *asked*
  // for after a refusal, where coming back as somebody else is the point.
  if (redirectResult?.account) {
    msalInstance.setActiveAccount(redirectResult.account);
    // And it makes any refusal in the URL stale. MSAL returns to the page the
    // sign-in started from, so an operator refused as one account and signed in
    // as another lands back on `/login?denied=…` holding a good session — where
    // the login guard, honouring `denied`, would keep them.
    const cleaned = withoutDenial(window.location.href);
    if (cleaned) {
      window.history.replaceState(null, '', cleaned);
    }
  }

  const root = document.getElementById('root');
  if (!root) {
    throw new Error('Missing #root element');
  }
  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </ErrorBoundary>
    </React.StrictMode>,
  );
});
